









































































import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { ClaimService } from "../../services/ClaimService";
import SearchFilters from "@/components/search-metadata/SearchFilters.vue";
import SearchResults from "@/components/search-metadata/SearchResults.vue";
import Accordion from "@components/shared/Accordion.vue";
import Pagination from "@/components/shared/Pagination.vue";
import { IPagination } from "@/model/component.model";
import { MetadataField, PresetField } from "@/model/data.model";
import { getStoreId } from "@/services/AuthService";
import OpenClaimModal from "./OpenClaimModal.vue";
import { isEmpty } from "lodash";

@Component({
  components: {
    SearchFilters: SearchFilters,
    Accordion: Accordion,
    Pagination: Pagination,
    OpenClaimModal,
    SearchResults
  }
})
export default class ClaimManagement extends Vue {
  filters: MetadataField[] = [];
  presets: PresetField[] = [];
  search: any = {};
  operations: any = {} as any;
  results: any[] = [];
  selectedRow: any = null;
  totalPages = 0;
  showOpenClaimModal = false;
  firstSearchExecuted = false;

  loading = false;
  errors: string[] = [];

  sortParams: Record<string, "asc" | "desc"> = {};

  pagination: IPagination = {
    currentPage: 1,
    perPage: 25,
    totalElements: 0,
    perPageOptions: [25, 50, 100, 200, 500]
  };

  get currentCustomerId() {
    return this.$store.getters["customer/info"].customerId;
  }

  get canDeleteIncident() {
    return (item: any) => {
      if (!item.active) return false;
      if (item.customerId !== this.currentCustomerId) return false;
      return item.claim === null;
    };
  }

  async created() {
    const metadata = await ClaimService.getClaimMetadata();
    this.filters = metadata.filters;
    this.presets = metadata.presets;
  }

  @Watch("search", { immediate: true, deep: true })
  executeFirstSearch() {
    if (!isEmpty(this.search) && !this.firstSearchExecuted) {
      this.searchClaims();
    }
  }

  get sortBy(): string | undefined {
    const entries = Object.entries(this.sortParams);
    if (!entries.length) return undefined;
    return entries.map(([field, dir]) => `${field} ${dir}`).join(",");
  }

  async searchClaims(loadAllPages = false) {
    this.results = [];
    this.errors = [];

    if (this.pagination.currentPage < 1) this.pagination.currentPage = 1;

    if (this.errors.length > 0) {
      console.error("claims search: validation failed", this.errors);
      return;
    }

    const filter: any = await this.getFiltersParameters();

    const effectivePageSize = loadAllPages ? 10000 : this.pagination.perPage;

    this.loading = true;
    try {
      const res: any = await ClaimService.getClaims(
        filter,
        this.pagination.currentPage - 1,
        effectivePageSize,
        this.sortBy
      );

      this.results = res.content || [];
      this.selectedRow = null;
      this.pagination.totalElements = res.totalElements ?? this.results.length;
      this.totalPages = res.totalPages ?? 1;
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  onPaginationChanged(newPagination: IPagination) {
    this.pagination = { ...this.pagination, ...newPagination };
    this.searchClaims();
  }

  async deleteIncident(item: any) {
    try {
      await this.$alerts
        .confirm(this.$t("delete_incident_confirm"))
        .then(async (val: boolean) => {
          if (val) {
            await ClaimService.prototype.deleteIncident([item.id]);
            await this.searchClaims();
          }
        });
    } catch (err) {
      console.log(err);
    }
  }

  async getFiltersParameters(): Promise<any> {
    const searchParams: any[] = [];
    const orClauses: any[] = [];

    this.filters.forEach((el: any) => {
      if (!Object.prototype.hasOwnProperty.call(this.search, el.field)) {
        return;
      }

      const searchValue = this.search[el.field];

      if (el.field.indexOf("customField.") !== -1) {
        const customFieldValue = searchValue;
        const field = el.field.replace("customField.", "");

        if (field === "storeId" || field === "documentHead.storeId") {
          if (customFieldValue == null || customFieldValue === true) {
            const _value = getStoreId();
            searchParams.push({
              field,
              type: "INTEGER",
              operation: "=",
              values: [_value]
            });
          }
        }
        return;
      }

      if (el.type === "DATE") {
        if (!searchValue) return;

        const dateStart = searchValue.dateStart;
        if (dateStart != null && dateStart !== "Invalid Date") {
          searchParams.push({
            field: el.field,
            type: "DATE",
            operation: ">=",
            values: [dateStart]
          });
        }

        const dateEnd = searchValue.dateEnd;
        if (dateEnd != null && dateEnd !== "Invalid Date") {
          searchParams.push({
            field: el.field,
            type: "DATE",
            operation: "<=",
            values: [dateEnd]
          });
        }

        return;
      }

      if (el.type === "LOCALDATETIME") {
        if (
          searchValue !== null &&
          searchValue.dateStart === null &&
          searchValue.dateEnd === null
        ) {
          this.search[el.field] = null;
          return;
        }

        if (searchValue !== null) {
          const dateStart = new Date(searchValue.dateStart);
          if (dateStart != null) {
            searchParams.push({
              field: el.field,
              type: "LOCALDATETIME",
              operation: ">=",
              values: [dateStart]
            });
          }

          const dateEnd = new Date(
            new Date(searchValue.dateEnd).setUTCHours(23, 59, 59, 999)
          );
          if (dateEnd != null) {
            searchParams.push({
              field: el.field,
              type: "LOCALDATETIME",
              operation: "<=",
              values: [dateEnd]
            });
          }
        }

        return;
      }

      const _type = el.type;
      let _value = searchValue;

      if (_value == null || _value === "") {
        return;
      }

      if (el.type === "CHOOSE_ONE_INTEGER" || el.type === "INTEGER") {
        _value = parseInt(_value, 10);
      }

      if (!Array.isArray(_value)) {
        _value = [_value];
      }

      let operation = "=";

      if (el.type === "TEXT" && el.typeSpec === "..") {
        operation = "._.";
      }

      if (el.type === "START_TEXT_UPPERCASE") {
        _value[0] = String(_value[0]).toUpperCase();
        if (_value[0].includes("*")) {
          operation = "_.U";
        } else {
          operation = "=";
        }
      }

      if (this.operations[el.field]) {
        operation = this.operations[el.field];
      }

      if (el.field === "multiPackage") {
        if (this.search[el.field] === false) {
          searchParams.push({
            field: "totalNumberOfPackages",
            type: "INTEGER",
            operation: "=",
            values: [1]
          });
        } else if (this.search[el.field] === true) {
          searchParams.push({
            field: "totalNumberOfPackages",
            type: "INTEGER",
            operation: ">",
            values: [1]
          });
        }
        return;
      }

      if (!_value.length) {
        return;
      }

      if (el.type === "BOOLEAN" && this.search[el.field] === false) {
        searchParams.push({
          field: el.field,
          type: _type,
          operation,
          values: [false]
        });
        return;
      }

      if (el.type === "NOT_PRESENCE") {
        if (this.search[el.field] === true) {
          searchParams.push({
            field: el.field,
            type: _type,
            operation,
            values: _value
          });
        }
        return;
      }

      searchParams.push({
        field: el.field,
        type: _type,
        operation,
        values: _value
      });
    });

    const data: any = {
      and: searchParams,
      or: orClauses
    };

    return data;
  }

  openIncidentDetails(row: any) {
    const id = row?.id;
    if (!id) return;

    this.$router.push({
      name: "IncidentDetails",
      params: { id }
    });
  }
}
